import { useRef } from 'react';
import { useFramework, useScale } from '@/framework';
import { type ITouchKitEvent } from '@/utils/event-kit';
import { getLayerInfoWithRotate, isRectOutOfBounds, traverseLayers } from '@/utils/layer-kit';
import {
    DirectionType,
    REFERENCE_LINE_DIRECTION,
    REFERENCE_LINE_EVENT_NAMES,
    SCALE_ABSORB_DISTANCE,
    ScaleOperateType,
    SMART_GUIDE_EVENT_NAMES,
    type ILayerProps,
    type IPoint,
} from '@/version-types';
import { useMouseOperateState } from '../../states/mouse-operate-state';
import LineList from '../tools/line-list';
import {
    checkLayerNeedToAlign,
    getCurrentOperateLayer,
    getDirectionVector,
    getScaleAbsorbOffset,
} from '../tools/utils';

export const useScaleProxy = () => {
    const framework = useFramework();
    const scale = useScale();

    const referenceLines = useMouseOperateState()((state) => state.referenceLines);

    const { width, height } = framework.stage.getActiveStageSize();

    // 是否吸附中
    const isAdsorb = useRef<boolean>(false);
    // 吸附状态下的位移
    const adsorbDisplacement = useRef<IPoint>({ x: 0, y: 0 });

    const lineData = useRef<LineList>();

    const proxyBeforeScaling = (operateType: ScaleOperateType) => {
        const layer = getCurrentOperateLayer(framework);
        if (!layer) return;

        lineData.current = new LineList();

        // 把参考线添加到吸附的元素计算列表中
        if (referenceLines.length) {
            const stageIndex = framework.stage.getActiveStageIndex();
            const top = framework.getStageTop(stageIndex);

            // 多画布的情况, 需要把参考线转换到当前画布的坐标系中
            const correctedReferenceLines = referenceLines.map((line) => {
                if (line.direction === REFERENCE_LINE_DIRECTION.HORIZONTAL) {
                    return {
                        ...line,
                        position: line.position - top,
                    };
                }
                return line;
            });
            lineData.current.addReferenceLineData(correctedReferenceLines);
        }
        // 舞台边缘
        lineData.current.push({ width, height, left: 0, top: 0, rotate: 0, isStage: true, id: 'stage_' + Date.now() });

        let layers: ILayerProps[] = [];
        if (layer.parentId) {
            const parentLayer = framework.getLayer(layer.parentId);
            if (parentLayer) {
                layers = (parentLayer.children || []).filter(({ id }) => id !== layer.id);
            }
        } else {
            const originIndex = framework.stage.getActiveOriginIndex();
            layers = framework.getLayers(originIndex);
        }

        // 过滤不在可视范围内的图层
        const stageSize = framework.stage.getActiveStageSize();
        layers = layers.filter((layer) => {
            const layerRect = getLayerInfoWithRotate(layer);
            const isOutOfBounds = isRectOutOfBounds(layerRect, stageSize);
            return !isOutOfBounds;
        });

        const directionVector = getDirectionVector(operateType, layer.rotate);
        const layerRect = getLayerInfoWithRotate(layer);
        // 添加参与计算的图层
        traverseLayers(
            layers,
            layer,
            (item: ILayerProps) => {
                const checkLayerRect = getLayerInfoWithRotate(item);
                const needToAlign = checkLayerNeedToAlign(layerRect, checkLayerRect, directionVector);
                if (needToAlign) {
                    lineData.current!.push(item);
                }
            },
            false,
        );
    };
    const proxyScaling = ({
        displacementX,
        displacementY,
        offsetX,
        offsetY,
        directionX,
        directionY,
        oldLayer,
        operateType,
        keepRatio,
        e,
    }: {
        displacementX: number;
        displacementY: number;
        offsetX: number;
        offsetY: number;
        directionX: DirectionType;
        directionY: DirectionType;
        oldLayer: ILayerProps;
        operateType: ScaleOperateType;
        keepRatio: boolean;
        e: ITouchKitEvent;
    }) => {
        const layer = getCurrentOperateLayer(framework);
        if (!layer) return { x: 0, y: 0 };

        const rotate = layer.rotate;

        /**
         * 求出当前缩放值下, layer 宽高的变化, 用于 block 的计算
         * 因为拖动图层的左边或者上边调整的时候, 会改变图层的 left, top
         * 所以需要用调整后的 left, top 去计算才会准确
         */
        const leftOffset = directionX === DirectionType.OPPOSITE ? displacementX * directionX : 0;
        const topOffset = directionY === DirectionType.OPPOSITE ? displacementY * directionY : 0;

        // 当前鼠标调整后的图层尺寸(不包含吸附)
        const resizedLayer = {
            ...oldLayer,
            left: oldLayer.left + leftOffset,
            top: oldLayer.top + topOffset,
            width: Math.floor(oldLayer.width + displacementX),
            height: Math.floor(oldLayer.height + displacementY),
        };

        const oldLayerRect = getLayerInfoWithRotate(oldLayer);
        // 需要基于 resize 前的中心点进行旋转
        const centerPoint = {
            x: oldLayer.width / 2 + oldLayer.left,
            y: oldLayer.height / 2 + oldLayer.top,
        };
        const newLayerRect = getLayerInfoWithRotate(resizedLayer, centerPoint);

        const directionVector = getDirectionVector(operateType, rotate);

        const lineNearest = lineData.current?.getNearestLineForScale(
            {
                ...newLayerRect,
                rotate: 0,
            },
            directionVector,
        );

        if (lineNearest) {
            const adsorbDistance = Math.max(1, Math.ceil(SCALE_ABSORB_DISTANCE / scale));
            // x 轴吸附位移, 向左为负, 向右为正
            const nearestOffsetX = -lineNearest.vertical.distance;
            // y 轴吸附位移, 向上为负, 向下为正
            const nearestOffsetY = -lineNearest.horizontal.distance;
            // 进入吸附范围
            if (Math.abs(nearestOffsetX) < adsorbDistance || Math.abs(nearestOffsetY) < adsorbDistance) {
                if (!keepRatio) {
                    if (Math.abs(nearestOffsetX) < Math.abs(nearestOffsetY)) {
                        adsorbDisplacement.current.x = offsetX + nearestOffsetX;
                        adsorbDisplacement.current.y = offsetY;
                    } else {
                        adsorbDisplacement.current.x = offsetX;
                        adsorbDisplacement.current.y = offsetY + nearestOffsetY;
                    }
                    isAdsorb.current = true;
                } else if (!isAdsorb.current) {
                    let scaleOffset = { x: 0, y: 0 };

                    // 上下吸附
                    if (Math.abs(nearestOffsetX) < Math.abs(nearestOffsetY)) {
                        const directionX = directionVector.x === 0 ? 0 : directionVector.x < 0 ? -1 : 1;
                        // 吸附的偏移量
                        const additionalRectSize =
                            Math.abs(newLayerRect.width + nearestOffsetX * directionX) - oldLayerRect.width;
                        scaleOffset = getScaleAbsorbOffset({
                            rotate,
                            isHorizontal: false,
                            additionalRectSize,
                            oldLayer,
                            oldLayerRect,
                            mouseOffsetX: displacementX,
                            mouseOffsetY: displacementY,
                        });
                    }
                    // 左右吸附
                    else {
                        const directionY = directionVector.y === 0 ? 0 : directionVector.y < 0 ? -1 : 1;
                        // 吸附的偏移量
                        const additionalRectSize =
                            Math.abs(newLayerRect.height + nearestOffsetY * directionY) - oldLayerRect.height;
                        scaleOffset = getScaleAbsorbOffset({
                            rotate,
                            isHorizontal: true,
                            additionalRectSize,
                            oldLayer,
                            oldLayerRect,
                            mouseOffsetX: displacementX,
                            mouseOffsetY: displacementY,
                        });
                    }
                    adsorbDisplacement.current.y = scaleOffset.y;
                    adsorbDisplacement.current.x = scaleOffset.x;
                    isAdsorb.current = true;
                }
            } else {
                adsorbDisplacement.current.x = displacementX;
                adsorbDisplacement.current.y = displacementY;
                isAdsorb.current = false;
            }
        } else {
            adsorbDisplacement.current.x = displacementX;
            adsorbDisplacement.current.y = displacementY;
        }

        return adsorbDisplacement.current;
    };

    const proxyAfterScaling = (e: ITouchKitEvent) => {
        const layer = getCurrentOperateLayer(framework);
        if (layer) {
            // 对齐 UI 展示
            const lines = lineData.current?.getLines(layer, true);
            if (lines) {
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_LINES, lines);
            }

            // 高亮参考线
            const refLineIds = lineData.current?.getReferenceLines(layer);
            if (refLineIds) {
                framework.events.emit(REFERENCE_LINE_EVENT_NAMES.HIGHLIGHT, refLineIds);
            }
        }

        // 清除临时数据
        if (e.isEnd) {
            lineData.current = undefined;
            isAdsorb.current = false;
        }
    };

    return {
        proxyScaling,
        proxyBeforeScaling,
        proxyAfterScaling,
    };
};
